import { FilterMapped } from 'shared/utils/filters';
import { GRID_STRING_FILTER_VALUES } from 'components/common/dataGrid/filters/operators';
import type { IssueGridFilterType } from './issueFieldMappingConfig';

type IssueFilterMappedType = Extract<IssueGridFilterType, 'direct' | 'oneOfDirect'>;

export const ISSUE_STRING_OPERATOR_VALUES = GRID_STRING_FILTER_VALUES;

export type IssueStringFilterMappedType =
  (typeof ISSUE_STRING_OPERATOR_VALUES)[number];

export type IssueMappedFilterInput =
  | {
      kind: 'deleted';
      archivedOnly: boolean;
    }
  | {
      kind: 'dateRange';
      path: string;
      start?: number | null;
      end?: number | null;
    }
  | {
      kind: 'identifiers';
      path: string;
      type: IssueFilterMappedType;
      values: string[];
    }
  | {
      kind: 'string';
      path: string;
      type: IssueStringFilterMappedType;
      value?: string | string[];
    };

function toDateRangeFilterMapped(
  input: Extract<IssueMappedFilterInput, { kind: 'dateRange' }>
): FilterMapped | null {
  const toIsoDateOrNull = (value: unknown): string | null => {
    if (value == null) {
      return null;
    }

    if (
      !(value instanceof Date) &&
      typeof value !== 'number' &&
      typeof value !== 'string'
    ) {
      return null;
    }

    const date = value instanceof Date ? value : new Date(value);
    return Number.isFinite(date.getTime()) ? date.toISOString() : null;
  };

  const fromDate = toIsoDateOrNull(input.start);
  const toDate = toIsoDateOrNull(input.end);

  if (fromDate === null && toDate === null) {
    return null;
  }

  return {
    path: input.path,
    type: 'dateRange',
    value: { fromDate, toDate },
  };
}

function toIdentifiersFilterMapped(
  input: Extract<IssueMappedFilterInput, { kind: 'identifiers' }>
): FilterMapped | null {
  if (!input.values.length) {
    return null;
  }

  return {
    path: input.path,
    type: input.type,
    value: input.values.map((value) => ({ _id: value })),
  };
}

function toDeletedFilterMapped(
  input: Extract<IssueMappedFilterInput, { kind: 'deleted' }>
): FilterMapped {
  return {
    path: 'deleted',
    type: 'direct',
    value: [{ _id: input.archivedOnly ? '1' : '0' }],
  };
}

function toStringFilterMapped(
  input: Extract<IssueMappedFilterInput, { kind: 'string' }>
): FilterMapped | null {
  if (
    input.type !== 'isEmpty' &&
    input.type !== 'isNotEmpty' &&
    input.value == null
  ) {
    return null;
  }

  if (Array.isArray(input.value) && input.value.length === 0) {
    return null;
  }

  if (typeof input.value === 'string' && input.value.length === 0) {
    return null;
  }

  return {
    path: input.path,
    type: input.type,
    value: input.value,
  };
}

export function issueFiltersToFilterMapped(
  inputs: IssueMappedFilterInput[]
): FilterMapped[] {
  const result: FilterMapped[] = [];

  for (const input of inputs) {
    if (input.kind === 'deleted') {
      result.push(toDeletedFilterMapped(input));
      continue;
    }

    if (input.kind === 'dateRange') {
      const mapped = toDateRangeFilterMapped(input);
      if (mapped) {
        result.push(mapped);
      }
      continue;
    }

    if (input.kind === 'string') {
      const mapped = toStringFilterMapped(input);
      if (mapped) {
        result.push(mapped);
      }
      continue;
    }

    const mapped = toIdentifiersFilterMapped(input);
    if (mapped) {
      result.push(mapped);
    }
  }

  return result;
}
